import * as angular from "angular";
import { Field } from "../../models/field.model";
import { VoterFileConflictService } from "../../services/voter-file-conflict.service";
import { DialogUtils } from "../../helper/dialog.helper";
import { ScreenUtils } from "../../helper/screen.helper";
import { Utils } from "../../helper/utils.helper";

let VoterFileConflictComponent = {
    selector: "vcVoterFileConflict",
    templateUrl:
        "/angular/components/voter-file/voter-file-conflict.component.html",
    bindings: {},
    controller: class VoterFileConflictController {
        private readonly conflictService: VoterFileConflictService;
        private readonly __env: any;
        private readonly dialog: DialogUtils;
        public readonly utils: Utils;
        public readonly screen: ScreenUtils;

        public conflicts: object[] = [];
        public selectedItem: any = null;
        public initial_loading = true;
        public totalConflicts = 0;
        public matches_loading: boolean;
        public conflictMatches: object[];
        public loading: boolean;
        public infiniteItems: VoterFileConflictInfiniteItems;

        constructor(
            VoterFileConflictService: VoterFileConflictService,
            __env: any,
            DialogUtils: DialogUtils,
            Utils: Utils,
            ScreenUtils: ScreenUtils
        ) {
            this.conflictService = VoterFileConflictService;
            this.__env = __env;
            this.dialog = DialogUtils;
            this.utils = Utils;
            this.screen = ScreenUtils;
            this.infiniteItems = new VoterFileConflictInfiniteItems(
                this,
                this.conflictService
            );
        }

        $onInit() {
            this.getTotalConflicts();
        }

        getTotalConflicts(): void {
            this.conflictService.count_conflicts().then((response: object) => {
                const json = response["data"];
                this.totalConflicts = json.count;
            });
        }

        conflictSelected(conflict: any): void {
            this.matches_loading = true;
            if (conflict.erpcflstatus === "Conflict") {
                this.selectedItem = conflict;
                this.conflictService
                    .conflict_matches({ id: conflict.erpcflid })
                    .then((response: object) => {
                        const json = response["data"];
                        this.matches_loading = false;
                        if (json.success === true) {
                            this.conflictMatches = json.data;
                        }
                    });
            }
        }

        add(conflict: any, ev: any): void {
            // Appending dialog to document.body to cover sidenav in docs app
            let confirm = this.dialog
                .confirm()
                .title("Create New Person?")
                .textContent(
                    "This will create a new person using the data in the selected conflict"
                )
                .targetEvent(ev)
                .ok("Confirm")
                .cancel("Cancel");
            this.dialog.showPreConfiguredDialog(confirm).then(() => {
                this.conflictService
                    .add_voter({ erpcflid: conflict.erpcflid })
                    .then((response: object) => {
                        const json = response["data"];
                        if (json.success === true) {
                            conflict.erpcflstatus = "New";
                            this.conflictMatches = [];
                        }
                    });
            });
        }

        replace(conflict: any, erpcflid: string | number, ev: any): void {
            // Appending dialog to document.body to cover sidenav in docs app
            let confirm = this.dialog
                .confirm()
                .title("Replace Existing Person?")
                .textContent(
                    "This action will permanently replace the contact?"
                )
                .targetEvent(ev)
                .ok("Confirm")
                .cancel("Cancel");
            this.dialog.showPreConfiguredDialog(confirm).then(() => {
                this.conflictService
                    .replace_voter({ erpcflid: erpcflid, hoid: conflict.hoid })
                    .then((response: object) => {
                        const json = response["data"];
                        if (json.success === true) {
                            this.selectedItem.erpcflstatus = "Merged";
                            this.conflictMatches = [];
                        }
                    });
            });
        }

        delete(conflict: any, ev: any): void {
            // Appending dialog to document.body to cover sidenav in docs app
            this.dialog
                .delete({
                    fullscreen: true,
                    targetEvent: ev,
                    skipHide: true,
                    locals: {
                        bodyText: "This action will be permanent",
                        title: `Discard Conflict?`,
                    },
                })
                .then(() => {
                    this.conflictService
                        .delete_conflict({ id: conflict.erpcflid })
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success === true) {
                                this.infiniteItems.items.splice(
                                    this.infiniteItems.items.indexOf(conflict),
                                    1
                                );
                                this.conflictMatches = [];
                                this.totalConflicts--;
                            }
                        });
                });
        }

        customFieldToString(customFields: Field[]): string {
            if (customFields === undefined) {
                return "";
            }
            return customFields
                .filter((field: Field) => {
                    return (
                        !field.hasOwnProperty("hocfid") || field.hocfid !== null
                    );
                })
                .map((field: Field) => {
                    return field.cfname + ": " + field.value;
                })
                .join(", ");
        }

        deleteAll(ev: any): void {
            // Appending dialog to document.body to cover sidenav in docs app
            this.dialog
                .delete({
                    fullscreen: true,
                    targetEvent: ev,
                    skipHide: true,
                    locals: {
                        bodyText: "This action will be permanent",
                        title: `Discard All Conflicts?`,
                    },
                })
                .then(() => {
                    this.loading = true;
                    this.conflictService
                        .delete_all_conflicts()
                        .then((response: object) => {
                            const json = response["data"];
                            if (json.success === true) {
                                this.totalConflicts = 0;
                                this.infiniteItems.reset();
                                this.getTotalConflicts();
                                this.conflictMatches = [];
                            }
                        });
                });
        }
    },
};

class VoterFileConflictInfiniteItems {
    public finished = false;
    public numLoaded_ = 0;
    public toLoad_ = 0;
    public highest_erpcflid: string | number | null = null;
    public loading = false;
    public items: object[] = [];
    private readonly conflictController: any;
    private readonly conflictService: VoterFileConflictService;

    constructor(
        conflictController: any,
        conflictResource: VoterFileConflictService
    ) {
        this.conflictController = conflictController;
        this.conflictService = conflictResource;
    }

    reset(): void {
        this.items = [];
        this.numLoaded_ = 0;
        this.toLoad_ = 0;
        this.highest_erpcflid = null;
    }

    getItemAtIndex(index: number | null): object {
        if (index > this.numLoaded_ - 1 && !this.loading) {
            this.fetchMoreItems_(index);
            return null;
        }
        return this.items[index];
    }

    // Required.
    // For infinite scroll behavior, we always return a slightly higher
    // number than the previously loaded items.
    getLength(): number {
        if (this.conflictController.totalConflicts === 0) {
            return 0;
        }
        if (
            this.conflictController.totalConflicts !== 0 &&
            this.numLoaded_ + 5 > this.conflictController.totalConflicts
        ) {
            return this.conflictController.totalConflicts;
        }
        return this.numLoaded_ + 5;
    }

    fetchMoreItems_(index: number): void {
        if (this.toLoad_ < index && !this.finished) {
            this.loading = true;
            this.conflictService
                .conflicts({ id: this.highest_erpcflid })
                .then((response: object) => {
                    const json = response["data"];
                    this.conflictController.initial_loading = false;
                    if (json.success === true) {
                        this.toLoad_ += 25;
                        this.numLoaded_ = this.toLoad_;
                        this.conflictController.conflicts = json.data;
                        this.highest_erpcflid =
                            this.conflictController.conflicts[
                                this.conflictController.conflicts.length - 1
                            ].erpcflid;
                        this.items = this.items.concat(json.data);
                        if (!this.conflictController.selectedItem) {
                            this.conflictController.conflictSelected(
                                this.items[0]
                            );
                        }
                        if (json.data.length !== 25) {
                            this.finished = true;
                        }
                        this.loading = false;
                    } else {
                        this.loading = false;
                        this.finished = true;
                    }
                });
        }
    }
}

angular
    .module("voterFile")
    .component(VoterFileConflictComponent.selector, VoterFileConflictComponent);
